import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useOverlayState } from '../context/OverlayContext';
import { AnimatePresence, motion } from 'framer-motion';
import { checkVybeLimits, VybeLimits } from '../utils/vybeLimits';

interface VybeCounterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  onStartVybe?: () => void;
  buttonRect?: DOMRect | null;
}

const VybeCounterModal: React.FC<VybeCounterModalProps> = ({ isOpen, onClose, onUpgrade, onStartVybe }) => {
  const { currentUser, accessToken } = useAuth();
  const navigate = useNavigate();
  useOverlayState(isOpen, 'vybe-counter-modal');
  const [success, setSuccess] = useState(false);
  const [vybeLimits, setVybeLimits] = useState<VybeLimits | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    const fetchLimits = async () => {
      if (!currentUser) return;
      const isSubscribed = currentUser.subscriptionTier === 'subscribed';
      const limits = await checkVybeLimits(currentUser.id, isSubscribed);
      setVybeLimits(limits);
    };
    if (isOpen) {
      fetchLimits();
    }
  }, [isOpen, currentUser]);

  useEffect(() => {
    if (isOpen) {
      setSuccess(false);
    }
  }, [isOpen]);

  const handleStartVybe = () => {
    onClose();
    if (onStartVybe) {
      onStartVybe();
    }
  };

  const portalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          <div
            className="nav-popover-backdrop"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="nav-popover-panel rounded-2xl border border-theme-cards bg-theme-cards shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
          <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <Sparkles size={20} className="mr-2 text-theme-primary" />
              <h2 className="text-lg font-semibold text-white">Vybe Credits</h2>
            </div>
            <button
              onClick={onClose}
              className="text-theme-text-secondary hover:text-white p-1"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {success ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check size={24} className="text-green-500" />
              </div>
              <h3 className="text-base font-semibold mb-2 text-white">Success!</h3>
              <p className="text-theme-text-secondary text-sm">
                Your credits have been added.
              </p>
            </div>
          ) : vybeLimits ? (
            <>
              <div className="mb-4">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  onClick={() => {
                    if (vybeLimits.create.remaining > 0) {
                      onClose();
                      if (onStartVybe) {
                        onStartVybe();
                      }
                    }
                  }}
                  className={`bg-gradient-to-br ${
                    vybeLimits.create.remaining === 0
                      ? 'from-red-500/10 to-red-600/5 border-red-500/30'
                      : 'from-green-500/10 to-green-600/5 border-green-500/30 cursor-pointer hover:from-green-500/20 hover:to-green-600/10 transition-all'
                  } border rounded-xl p-6 text-center`}
                >
                  <div className="flex justify-center mb-3">
                    <div className={`p-2 rounded-lg ${
                      vybeLimits.create.remaining === 0 ? 'bg-red-500/20' : 'bg-green-500/20'
                    }`}>
                      <Sparkles size={20} className={vybeLimits.create.remaining === 0 ? 'text-red-400' : 'text-green-400'} />
                    </div>
                  </div>
                  <div className={`text-3xl font-bold mb-1 ${
                    vybeLimits.create.remaining === 0 ? 'text-red-400' : 'text-white'
                  }`}>
                    {vybeLimits.create.remaining}/{vybeLimits.create.monthly + vybeLimits.create.tokens}
                  </div>
                  <div className="text-sm text-white/70 mb-1">
                    Vybes You Can Host
                  </div>
                  {vybeLimits.create.remaining === 0 && (
                    <div className="text-xs text-red-400 mt-2">
                      Out of Host credits
                    </div>
                  )}
                </motion.div>
              </div>

              <div className="text-center mb-4">
                <p className="text-xs text-white/50">
                  {vybeLimits.create.tokens > 0
                    ? 'Includes bonus credits from top-ups'
                    : 'Resets monthly on the 1st'}
                </p>
              </div>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClose();
                    navigate('/myAccount?tab=subscription');
                  }}
                  className="w-full bg-gradient-to-r from-red-500 to-pink-600 text-white py-3 rounded-full hover:from-red-600 hover:to-pink-700 transition-colors flex items-center justify-center font-semibold text-sm"
                >
                  <Sparkles size={16} className="mr-2" />
                  Get More Vybe Credits
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full mx-auto" />
            </div>
          )}
          </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(portalContent, document.body);
};

export default VybeCounterModal;
